import {
  BARN_CHARGE_PERMISSION,
  CHART_PANELS,
  CLINICAL_RECORD_PERMISSION,
  DEA_LOG_PERMISSION,
  EXPIRED_LOT_PERMISSION,
  INVENTORY_PRICING_PERMISSION,
  ISSUE_CREDIT_PERMISSION,
  LINK_HORSE_PERMISSION,
  RECORD_PAYMENT_PERMISSION,
  RECORD_ARCHIVE_PERMISSION,
  REFUND_PERMISSION,
  SCREEN_ACCESS,
  TEAM_MEMBERS_PERMISSION,
  canManageScreen,
  canViewScreen,
  screenAccessUnknown,
} from './screenAccess';
import type { ChartPanelKey } from './screenAccess';
import type { UserClaims } from './useRBAC';

/**
 * WHICH WRITE CONTROLS A PAGE RENDERS (INV-108, the in-page half).
 *
 * A1 stopped a person opening a screen they cannot READ. This is the other half of the same
 * complaint: somebody who may legitimately open a screen still met every Add, Edit and Delete on
 * it, filled the form, and was refused on submit. The rule here is one line long —
 *
 *   a control is rendered only if the person holds EXACTLY what the SERVER requires for the
 *   write that control triggers
 *
 * — and both halves of "exactly" are load-bearing. Fewer terms than the handler and the button
 * 403s on first click, which is the defect. MORE terms, or a `claims.role` check standing in for
 * a permission, and the button never appears for somebody an owner has granted the slug to: a
 * grant that silently does nothing, which is worse, because nobody files a bug about a button
 * they have never seen.
 *
 * WHY PURE FUNCTIONS IN ONE FILE, rather than `hasPermission('…')` inline on each page. These are
 * 1,000–3,700-line manager-built screens; a slug spelled inline in one of them is unreviewable and
 * untestable without mounting the whole page. Here the entire control map is one screenful, every
 * entry cites the controller line it copies, and `pageControls.test.ts` walks every control against
 * every relevant holder in milliseconds. The pages import a single call and spread the result.
 *
 * HIDE, NEVER DISABLE. A disabled button still advertises a capability and still reads as "broken
 * for me"; the product owner asked for the option not to be there. An empty state keeps its text
 * and loses its call to action.
 *
 * ⚠ AND A CONTROL IS HIDDEN ONLY ON A KNOWN ANSWER THAT LACKS THE SLUG. Every map is entered
 * through `controlsFor` below, which takes the HOOK STATE rather than a bare `hasPermission` and
 * shows everything while the answer is pending, unreadable, or attached to nobody. That is what
 * every one of these pages did before this work existed, it is what the server's own authority
 * makes safe, and it is the difference between a mirror and a second enforcement point that is
 * wrong more often than the first.
 */

/** Every control map is a flat record of booleans, so a test can walk one without knowing its shape. */
export type ControlMap = Readonly<Record<string, boolean>>;

const freeze = <T extends ControlMap>(map: T): T => Object.freeze(map);

export type HasPermission = (permission: string) => boolean;

/**
 * WHAT THE HOOK KNOWS RIGHT NOW — the structural subset of `useRBAC()`'s return these maps read.
 *
 * A page passes the hook's result straight in (`const rbac = useRBAC(); const can =
 * clientsControls(rbac);`), so there is no second place to keep in step and no way to hand a map
 * a `hasPermission` without the three flags that say whether it means anything yet.
 */
export interface ControlAccess {
  hasPermission: HasPermission;
  loading: boolean;
  permissionsPending: boolean;
  permissionsUnavailable: boolean;
  claims: UserClaims | null;
}

/** The pending/unknown answer: every control shown. Module-level so the maps keep a stable
 *  identity across renders in that state, exactly as they do in the answered one. */
const SHOW_EVERY_CONTROL: HasPermission = () => true;

/**
 * IS THERE AN ANSWER TO HIDE A CONTROL ON?
 *
 * Three ways there is not, and each one produced a live defect before this gate existed:
 *
 *  - `loading` — this consumer's own token round trip is open. It is FALSE-negative territory:
 *    `hasPermission` answers false for everything, so every page painted "holds nothing" first
 *    and popped its buttons in a beat later, for every user, on every visit.
 *  - `permissionsPending` — the shared `/users/me` read has not answered for this uid yet.
 *  - `claims === null` with `loading` closed — IDENTITY UNKNOWN (INV-107 trap (a)): the token
 *    refresh rejected and nothing was cached, so we have stopped asking and still do not know
 *    who this is. It is not a refusal and must not render as one.
 *
 * …plus `screenAccessUnknown`, the read that FAILED outright. ⚠ That predicate, and not the bare
 * `permissionsUnavailable` flag, on purpose: a TRANSIENT failure KEEPS the set the store already
 * held for this uid (`mePermissions.publishPermissionsUnavailable`), so the flag is routinely
 * true while every answer is still good. Reading the flag alone would throw that answer away and
 * show a narrowed person every control on a blip — and it would make the page disagree with its
 * own `<ScreenRoute>`, which decides on exactly this predicate.
 */
export const controlAnswerKnown = (state: ControlAccess): boolean =>
  !state.loading &&
  !state.permissionsPending &&
  state.claims !== null &&
  !screenAccessUnknown(state.claims, state.permissionsUnavailable);

/** The one entry point every map below goes through. */
const controlsFor = (state: ControlAccess): HasPermission =>
  controlAnswerKnown(state) ? state.hasPermission : SHOW_EVERY_CONTROL;

/**
 * CLIENTS — `pages/Clients/index.tsx`.
 *
 * `manage` (`manage:clients`) covers create/edit/delete: clients.controller.ts `create` :75-76,
 * `update` :93-94, `delete` :133-134. Everything else on the page is somebody else's route.
 */
export const clientsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.clients, has);
  return freeze({
    /** Toolbar "Add", the empty-state "Add Your First Client", the mobile FAB. */
    add: manage,
    /** Row "Edit" (the pencil in the detail header). */
    edit: manage,
    /** Row "Delete". */
    remove: manage,
    /**
     * "Link horse" / "Link Your First Horse". clients.controller.ts `linkHorseWithRole` :140-144
     * and `unlinkHorseRole` :163-164 gate on the PATIENT slug — linking is an ownership edit, and
     * somebody who may edit a client is not thereby allowed to re-own a horse.
     */
    linkHorse: has(LINK_HORSE_PERMISSION),
    /** "Issue Credit" → credits.controller.ts `create` :15-18 (`manage:discounts`). */
    issueCredit: has(ISSUE_CREDIT_PERMISSION),
    /** "View Invoices" deep link into /billing, whose list read is `view:billing`. */
    openBilling: has('view:billing'),
    /** The associated-horse rows link into the patient chart (`view:patients`). */
    openPatientChart: has('view:patients'),
  });
};

/** PATIENTS — `pages/PatientList/index.tsx`. horses.controller.ts `create` :30-31, `update` :156-157. */
export const patientsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.patients, has);
  return freeze({
    /** Toolbar Add (`data-tour="horse-add-button"`). */
    add: manage,
    /** The empty state's "Add the first patient" call to action; its text stays either way. */
    addFirst: manage,
    /**
     * The pencil on each `PatientCard` in the grid view, which opens the same dialog Add does and
     * saves through `PUT /horses/:id` (`manage:patients`). It is the one edit on this screen that
     * is NOT in the toolbar, which is exactly how it was missed: a narrowed person met an Edit
     * icon on every tile and a form that refused on save.
     */
    edit: manage,
    /** The unmatched lab and imaging results card: unallocated-results.controller.ts `list`. */
    unmatchedResults: canViewScreen(SCREEN_ACCESS.resultsInbox, has),
  });
};

/**
 * SCHEDULE — `pages/Appointments/index.tsx` + `AddAppointmentDialog`.
 *
 * ⚠ TIME BLOCKS ARE NOT `manage:appointments`, AND THAT IS NOT AN OVERSIGHT.
 * `time-blocks.controller.ts` declares NO permission on any handler — create, update and remove
 * are open to any authenticated clinic member — and `role-guard-coverage.spec.ts` exempts them
 * deliberately, in writing: "Any member creates PROVIDER blocks (front-desk/tech workflow) …
 * manage:appointments is held by every role, CSR included, so a decorator denies nobody." The
 * privileged case is payload-dependent — a CLINIC CLOSURE (`vetId: null`, ADR-0028) is
 * OWNER/ADMIN-only, enforced by `TimeBlocksService.assertClosureRole`, which no decorator can
 * express. So: no permission gate on the block controls, and the page's existing OWNER/ADMIN
 * closure checks stay exactly as they are, because there the SERVER really does gate on a role.
 */
export const scheduleControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.schedule, has);
  return freeze({
    /** Toolbar "Add", the empty day's "Book First Appointment". appointments.controller.ts :33-38. */
    add: manage,
    /**
     * A click on an empty calendar slot (daily and weekly grids), which opens the same add dialog.
     * (A month cell only opens that day, so it needs no gate.) Without the slug the click must do
     * NOTHING — a dialog that opens and refuses on save is the defect in its most expensive form,
     * because the form is long.
     */
    addFromSlot: manage,
    /** The appointment detail dialog's "Edit". appointments.controller.ts `update` :148-149. */
    edit: manage,
    /** …and its "Delete". appointments.controller.ts `remove` :187-188. */
    remove: manage,
    /**
     * "Generate Invoice" on the appointment detail dialog →
     * billing.controller.ts `generateFromAppointment` :892-893 (`manage:charges`). A money write
     * reached from the Schedule, so it asks the money slug and not the schedule's.
     */
    generateInvoice: has('manage:charges'),
    /** The panel's "Open chart" → /patients/:id, whose read is `view:patients`. */
    openPatientChart: has('view:patients'),
    /** New appointment's "New owner" → clients.controller.ts `create` (`manage:clients`). */
    newOwner: has('manage:clients'),
    /** New appointment's "New patient" → horses.controller.ts `create` (`manage:patients`). */
    newPatient: has('manage:patients'),
  });
};

/**
 * BARNS — `pages/Barns/index.tsx`, `BarnDetail.tsx`.
 *
 * barns.controller.ts: `create` :39-40, `update` :45-46, `archive` :52-53, `restore` :58-59,
 * `assignHorses` :65-66 — all `manage:patients`. "Remove from barn" is a `PUT /horses/:id`
 * (horses.controller.ts `update` :156-157), the same slug.
 */
export const barnsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.barns, has);
  return freeze({
    /** "New barn" on the list; "Edit" in the row and on the detail header. */
    add: manage,
    edit: manage,
    /** Row + header "Archive"/"Restore". */
    archive: manage,
    /** Detail "Add horses" and each row's "Remove from barn". */
    assignHorses: manage,
    removeHorse: manage,
    /** "Schedule appointment" opens the appointment dialog → appointments.controller.ts :33-38. */
    scheduleAppointment: has('manage:appointments'),
    /**
     * "Run Barn Operation" navigates to /barn-operations, whose only reason to exist is the
     * charge submit — so it asks the slug that submit asks, not merely the screen's read slug.
     */
    runBarnOperation: has(BARN_CHARGE_PERMISSION),
  });
};

/**
 * BARN VISIT WORKSPACE — `pages/Barns/BarnVisitWorkspace.tsx`.
 *
 * Two unrelated writes on one screen: the clinical note (medical-records.controller.ts `create`
 * :18-19 / soap-notes.controller.ts `create` :43-44) and the invoice submit
 * (billing.controller.ts `createBarnOperations` :235-236).
 */
export const barnVisitControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** "Quick note" → POST /medical-records. */
    quickNote: has(CLINICAL_RECORD_PERMISSION),
    /** "Start SOAP" → POST /soap-notes. */
    startSoap: has(CLINICAL_RECORD_PERMISSION),
    /** "Generate invoices & statements" → POST /billing/barn-operations. */
    submit: has(BARN_CHARGE_PERMISSION),
    /** "Open chart" is a read link into the patient chart. */
    openPatientChart: has('view:patients'),
  });
};

/** BARN OPERATIONS — `pages/Billing/BarnOperations.tsx`. billing.controller.ts :235-236. */
export const barnOperationsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** "Create invoices" / the submit button. Everything else on the page composes its payload. */
    submit: canManageScreen(SCREEN_ACCESS.barnOperations, has),
  });
};

/**
 * VACCINE LOTS — `components/Vaccination/VaccinationLotPicker.tsx` (the lot step of the visit, SOAP and Barn
 * Operations dialogs) and `VaccinationDialog.tsx`, which holds the save. The server refuses an expired lot without the
 * slug on every vaccination write door (`vaccination-lot-patch.ts` `assertExpiredLotPermitted`, ADR-0047 A5).
 */
export const vaccinationLotControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** The "This lot was given. Record it anyway." tick under an expired lot. */
    recordExpiredLot: has(EXPIRED_LOT_PERMISSION),
  });
};

/**
 * REFILL REQUESTS — `pages/RefillRequests/index.tsx`.
 *
 * refill-requests.controller.ts: `create` (the log route) :47-48, `verify` :54-56,
 * `approve` :62-64, `deny` :75-77 — one slug across all four.
 */
export const refillRequestsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.refillRequests, has);
  return freeze({
    /** "Log request". */
    log: manage,
    /** Row "Verify". */
    verify: manage,
    /** Row "Decision" (approve / deny / request recheck all live in that dialog). */
    decide: manage,
  });
};

/**
 * INVENTORY — `pages/Inventory/index.tsx` (+ the DEA Logs tab it hosts).
 *
 * inventory.controller.ts: `create` :21-22, `update` :130-131, `remove` :144-145,
 * `transfer` :68-69, `receive` :116-117 — `manage:inventory`; `updatePricing` :154-155 —
 * `manage:inventory_pricing`. The DEA tab's writes are dea-logs.controller.ts, which declares
 * `manage:medical_records` (see `DEA_LOG_PERMISSION`).
 */
export const inventoryControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.inventory, has);
  return freeze({
    /** Toolbar "Add Item". */
    add: manage,
    /** Row edit (pencil). */
    edit: manage,
    /** Row delete. */
    remove: manage,
    /** Row "Receive" stock → POST /inventory/:id/receive. */
    receive: manage,
    /** Toolbar "Transfer stock" → POST /inventory/transfer. */
    transfer: manage,
    /** The unpriced-item pricing prompt → PUT /inventory/:id/pricing. */
    pricing: has(INVENTORY_PRICING_PERMISSION),
    /** Lots dialog: correct or retire a lot → PUT /inventory/lots/:id (`updateLot`, manage:inventory). */
    editLot: manage,
    /** Lots dialog: recount a lot → POST /inventory/lots/:id/adjust (`adjustLot`, manage:inventory like the item count). */
    recountLot: manage,
    /** Lots dialog: lot details for stock received without them → POST /inventory/:id/assign-lot (manage:inventory, like receive). */
    addLotDetails: manage,
    /** DEA Logs tab: attest, add substance, add lot. */
    deaWrite: has(DEA_LOG_PERMISSION),
  });
};

/**
 * INVOICING — `pages/Billing/index.tsx`, `BulkMarkPaid.tsx`.
 *
 * billing.controller.ts: `create` :104-105, `update` :312-313, `remove` :885-886,
 * `generateFromAppointment` :892-893 — `manage:charges`. Payments are payments.controller.ts
 * `record` :40-41 and `recordBatch` :49-50; refunds/voids are `refund` :122-123 and
 * billing.controller.ts `voidInvoice` :367-368.
 *
 * ⚠ "BULK MARK PAID" IS A PAYMENT, NOT A STATUS EDIT — checked at the call site, not assumed from
 * the screen's name. `BulkMarkPaid.tsx` posts `/payments/record-batch` (`batchPaymentApi.record`),
 * so its gate is `manage:payments`. `PUT /billing/:id/status` (`manage:charges`) exists on the
 * server and has NO web caller at all — grep it. Gating that screen on `manage:charges` because
 * it is filed under Billing would have hidden it from exactly the front-desk role it is for.
 *
 * ⚠ THE DISCOUNT FIELDS ARE NOT `manage:discounts`. That slug is required by exactly one route in
 * the whole backend — `POST /credits` — and the invoice form's line and invoice-level discounts
 * ride on `POST`/`PUT /billing`, i.e. on `manage:charges`. Gating them separately would hide a
 * field from somebody the server would accept it from. (`InvoiceDetail.tsx` already gates on
 * `manage:charges`; this keeps the two consistent.)
 */
export const invoicingControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.invoicing, has);
  return freeze({
    /** The "New Invoice" split button (`data-tour="invoice-create-button"`) and its menu. */
    add: manage,
    /** Row edit. */
    edit: manage,
    /** Row delete. */
    remove: manage,
    /** Row "Record Payment", the selection bar's bulk modal, and `BulkMarkPaid`'s submit. */
    recordPayment: has(RECORD_PAYMENT_PERMISSION),
    /** The split menu's "Record bulk payment" entry → the same `/payments/record-batch` route. */
    bulkMarkPaid: has(RECORD_PAYMENT_PERMISSION),
    /** Refund / void. */
    refund: has(REFUND_PERMISSION),
  });
};

/**
 * TREATMENT SHEET — `pages/Hospital/index.tsx`, `TreatmentSheetPage.tsx`, `TreatmentGrid.tsx`,
 * `AddOrderSlideOver.tsx`.
 *
 * Every write across the six controllers of the treatment-sheet module declares the SAME slug,
 * `manage:medical_records`: treatment-sheet.controller.ts `create` (admit) :39-40, `update`
 * (move/discharge) :45-46, `remove` :51-52; stall.controller.ts `create` :28-29, `update` :34-35,
 * `remove` :40-41; treatment-order.controller.ts `create` :39-40, `update` :57-58, `remove`
 * :63-64; vital-entry.controller.ts `create` :20-21; administration-log.controller.ts
 * `administer` :26-27, `update` :32-33, `amend` :39-40; clinical-note.controller.ts `create`
 * :21-22, `update` :27-28.
 *
 * ⚠ NOT A ROLE. The page carried three `hasRole` checks; no handler in the module uses `@Roles`,
 * so each of those refused a technician an owner had granted `manage:medical_records` to. They
 * are re-pointed at the slug.
 */
export const treatmentSheetControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.treatmentSheet, has);
  return freeze({
    /** "Admit Patient" (stall card + board header). */
    admit: manage,
    /** The stall card's "Move to another stall". */
    move: manage,
    /** "Manage Locations", and the add/delete inside that dialog. */
    manageLocations: manage,
    /** Add / edit / delete a treatment order. */
    orders: manage,
    /** Record a vital. */
    vitals: manage,
    /** Administer a dose, and amend an administration. */
    administrations: manage,
    /** Add or edit a clinical note on the sheet. */
    notes: manage,
  });
};

/**
 * RESULTS INBOX — `pages/Inbox/InboxPage.tsx`.
 * unallocated-results.controller.ts `assign` :94-95, `reject` :112-113, `reopen` :130-131.
 */
export const resultsInboxControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const manage = canManageScreen(SCREEN_ACCESS.resultsInbox, has);
  return freeze({ assign: manage, reject: manage, reopen: manage });
};

/**
 * DASHBOARD — `pages/Dashboard/index.tsx`.
 *
 * The Dashboard is deliberately NOT in `SCREEN_ACCESS` (it is always reachable), so every tile and
 * every shortcut gates itself here. A tile whose data the person cannot read is not rendered AND
 * its query does not fire: `enabled:` false, so no refused request goes out at all. That matters
 * more here than anywhere else — this is the landing screen, so a narrowed person's every session
 * would otherwise open with four 403s.
 */
export const dashboardControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** Tiles, by the list read behind each one. */
    todaysAppointmentsTile: has('view:appointments'),
    unpaidInvoicesTile: has('view:billing'),
    patientsTile: has('view:patients'),
    activeClientsTile: has('view:clients'),
    /** Quick actions — each lands on a form whose submit needs the WRITE slug. */
    newAppointment: has('manage:appointments'),
    addHorse: has('manage:patients'),
    newInvoice: has('manage:charges'),
    /** "+ Add Record" → the clinical records area (medical-records.controller.ts `create`). */
    addRecord: has(CLINICAL_RECORD_PERMISSION),
    /** "View all" under today's appointments → /appointments, whose read is `view:appointments`. */
    viewAllAppointments: has('view:appointments'),
  });
};

/**
 * THE PATIENT CHART — `pages/ClinicalRecords/HorseProfileModern.tsx`, `HorseProfileClassic.tsx`
 * and every component they mount (`components/QuickActionStrip.tsx`,
 * `components/LabsDiagnosticsPanel.tsx`, `components/ProblemListPanel.tsx`,
 * `components/RecallRemindersPanel.tsx`, `components/PatientFiles/PatientFilesTab.tsx`).
 *
 * TWO LAYOUTS, ONE MAP. Modern and Classic are independent 2,400–2,800-line components that show
 * the same chart two ways; every gate below is identical on both, so it lives once. Whichever
 * layout the person toggled to, the answer is the same one.
 *
 * ⚠ WHAT WENT WRONG HERE, because it is the general lesson rather than this screen's detail: the
 * chart was left out of A1/A2's sweep on the grounds that it was "already gated" — `/patients/:id`
 * IS behind `<ScreenRoute>` on `view:patients`. But that slug gates `GET /horses/:id`, the HORSE,
 * and nothing else the page shows: the product owner turned "View medical records" AND "Create &
 * edit medical records" off and still met **New SOAP Note**, still met **Add Lab / Diagnostic**,
 * and read **"No medical records yet"** where the sentence should have said he has no access. A
 * gated ROUTE says nothing about the panels inside it.
 *
 * ⚠ AND `canCreateSoapNote` WAS A ROLE QUARTET — `hasRole('VETERINARIAN') ||
 * hasRole('VETERINARY_TECHNICIAN') || hasRole('OWNER') || hasRole('ADMIN')` — while
 * `POST /soap-notes` declares `manage:medical_records` (soap-notes.controller.ts:44). Both
 * directions were live: a vet whose clinic revoked the slug still met the button and was refused
 * on save, and anybody an owner granted the slug to outside those four roles never saw it at all.
 * It is the permission now, exactly.
 */
export const patientChartControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  /** Every clinical WRITE on the chart: one slug, from twelve controllers. */
  const clinical = has(CLINICAL_RECORD_PERMISSION);
  return freeze({
    /**
     * Row-1 "Edit patient" (the pencil) and the horse form it opens →
     * horses.controller.ts `update` :157 (`PUT /horses/:id`). The PATIENT slug, not the clinical
     * one — this is the only pair on the chart where the distinction bites, and a CSR who may
     * keep the patient record straight without writing clinical notes is exactly who it is for.
     */
    editPatient: canManageScreen(SCREEN_ACCESS.patients, has),
    /** "Manage alerts" → alerts.controller.ts `create` :29 / `update` :41 / `remove` :60 — all
     *  `manage:patients`, because a medical alert is a field on the horse. */
    manageAlerts: canManageScreen(SCREEN_ACCESS.patients, has),
    /**
     * "New SOAP Note" (the QuickActionStrip's lead card on Modern, the tab-header button on
     * Classic) → soap-notes.controller.ts `create` :44. THE FIRST OF THE THREE DEFECTS: this was
     * the role quartet above.
     */
    newSoapNote: clinical,
    /** The timeline's / card's SOAP "Edit" → soap-notes.controller.ts `upsert` :147, `update`
     *  :131. Same slug as create, and a SEPARATE flag because the row pencil was reading
     *  `manage:patients` — the PATIENT slug on a clinical write. */
    editSoapNote: clinical,
    /**
     * Every other clinical ADD: Medication, Vaccine, Procedure, Visit Note, Log Call, and the
     * empty-state calls to action beside them. medications.controller.ts `create` :20,
     * vaccinations.controller.ts `create` :17, procedures.controller.ts `create` :15,
     * medical-records.controller.ts `create` :19, client-communications.controller.ts `create` :27.
     */
    addClinicalRecord: clinical,
    /** …and their Edit: medications `update` :69, vaccinations `update` :66, procedures `update`
     *  :55, medical-records `update` :67, client-communications `update` :49. */
    editClinicalRecord: clinical,
    /**
     * The med / vaccination / procedure row DELETE.
     *
     * ⚠ TWO SLUGS, ANDed, and that is the server's spelling rather than a precaution:
     * `@RequirePermissions('manage:medical_records', 'manage:medical_records_archive')` on
     * medications.controller.ts :101, vaccinations.controller.ts :98 and
     * procedures.controller.ts :87 — and `RoleGuard` reads a conjunction (`role.guard.ts:48`).
     * Gating this on the archive slug alone would offer a delete whose first click 403s for
     * anybody holding archive without the authoring slug.
     */
    removeClinicalRecord: clinical && has(RECORD_ARCHIVE_PERMISSION),
    /**
     * …AND THE FOURTH DELETE IN THE SAME FAMILY, which is NOT the conjunction above.
     *
     * `DELETE /medical-records/:id` declares `manage:medical_records_archive` ALONE
     * (medical-records.controller.ts:89), like the attachment delete and unlike the med /
     * vaccination / procedure ones. It soft-archives, and its own comment says all sixteen
     * archive handlers share one slug so a revoke closes the capability rather than five of its
     * sixteen doors.
     *
     * ⚠ IT IS THE ONE THE LABS PANEL CALLS. A "diagnostic" row there is a `MedicalRecord`
     * (type imaging / outside_lab / diagnostic), not a `LabResult`, so its Delete goes to THIS
     * route while the bloodwork row beside it goes to `lab-results::remove` — which declares the
     * AUTHORING slug alone. Two rows that look identical, two different gates, and the natural
     * inference ("diagnostic row, so the lab control") is wrong in both directions. Gating this
     * on `removeClinicalRecord` hid a delete from every holder of the archive slug who lacks the
     * authoring one, which the permission editor can produce: `REQUIRES_VIEW` ties
     * `manage:medical_records_archive` to `view:medical_records`, not to `manage:medical_records`.
     */
    removeRecord: has(RECORD_ARCHIVE_PERMISSION),
    /**
     * Labs & Diagnostics: "Add Lab / Diagnostic", the New-order menu, Import, per-row Edit and
     * Delete. THE SECOND DEFECT — the whole panel was ungated. lab-results.controller.ts
     * `create` :36, `update` :103, `remove` :128; keystone.controller.ts `createImagingRequest`
     * :1211; idexx.controller.ts `createLabRequest` :654. One slug across all five.
     *
     * ⚠ Since 2026-10 `lab-results.controller.ts remove` ARCHIVES and declares both slugs, so the
     * panel shows Archive only when this flag AND `removeRecord` hold.
     */
    labWrite: clinical,
    /** Problem List add / edit / resolve → problem-list.controller.ts `create` :13, `update` :45.
     *  Its `remove` archives and also needs `removeRecord` (2026-10). */
    problemWrite: clinical,
    /** Recall reminders add / edit / complete / delete / send-now → recall-reminders.controller.ts
     *  `create` :33, `update` :150, `remove` :173, `sendNow` :136. */
    recallWrite: clinical,
    /** "New Discharge", and the editor the timeline's Edit opens →
     *  discharge-summaries.controller.ts `create` :42, `update` :72. */
    dischargeWrite: clinical,
    /** Patient Files "Upload" → attachments.controller.ts `upload` :39. */
    uploadFile: clinical,
    /**
     * Patient Files "Remove" → attachments.controller.ts `remove` :161, which declares
     * `manage:medical_records_archive` ALONE. Already correct on that tab before this work; it is
     * lifted here so the map is the whole answer and the tab stops spelling a slug inline.
     */
    removeFile: has(RECORD_ARCHIVE_PERMISSION),
    /**
     * The archive kebab on a timeline row: the slug the sixteen archive routes declare, and
     * nothing else since 2026-09-28 (ADR-0035 A10). soap-notes `restore` stays
     * `@Roles(ADMIN, OWNER)`, and the chart offers no Restore.
     */
    archive: has(RECORD_ARCHIVE_PERMISSION),
    /** "New Invoice" inside the Invoices panel → billing.controller.ts `create` :105 and
     *  `generateFromHorse` — the WRITE slug, which is not the panel's read slug. */
    newInvoice: canManageScreen(SCREEN_ACCESS.invoicing, has),
    /**
     * The owner rows in the header are a READ LINK into /clients, whose list read is
     * `view:clients` (clients.controller.ts `findAll` :32) and which is itself behind
     * `<ScreenRoute>` — so without the slug the click landed on a no-access screen.
     *
     * ⚠ AND THERE IS DELIBERATELY NO `linkOwner` FLAG BESIDE IT. One was written, gated on
     * `LINK_HORSE_PERMISSION`, and the wiring test's orphan scan caught it: the chart has no
     * link-horse control at all — owners are edited through the patient form, which is
     * `editPatient` — so the flag was declared, tested against the right slug, and read by
     * nobody. That shape is worse than a missing gate (INV-108's own note on it), because
     * everything about it looks finished. It was deleted rather than given an invented consumer.
     */
    openClient: canViewScreen(SCREEN_ACCESS.clients, has),
    /**
     * "Share Records" (StableDocs) → stabledocs.controller.ts: the candidate
     * list, export, send, shares and settings READ all declare `view:medical_records`.
     */
    shareRecords: has('view:medical_records'),
    /**
     * "Include drafts" in the StableDocs dialog → stabledocs.controller.ts `canIncludeDrafts`, which
     * reads `manage:medical_records` off the claims and refuses a draft request without it.
     */
    shareDrafts: has(CLINICAL_RECORD_PERMISSION),
    /**
     * NOT A PANEL, SO IT HAS NO SENTENCE — just a query that must not fire. The low-stock read
     * (`inventory.controller.ts getLowStockItems` :59, `view:inventory`) only tints a medication
     * card, so a person without it loses a highlight rather than a section; what they must not
     * get is a 403 on every chart they open.
     */
    lowStockHighlight: canViewScreen(SCREEN_ACCESS.inventory, has),
    /** Same shape: the "last visit" stamp reads appointments.controller.ts `findAll` :33-38
     *  (`view:appointments`). A missing stamp, not a missing panel. */
    lastVisit: canViewScreen(SCREEN_ACCESS.schedule, has),
  });
};

/**
 * WHICH CHART PANELS THIS PERSON MAY READ (INV-108 (b) + (d), one level below the route).
 *
 * `true` means RENDER THE PANEL AND LET ITS QUERIES FIRE; `false` means render
 * `noAccessMessage(CHART_PANELS[key].noun)` in place — instead of the panel AND instead of its
 * empty state — and set `enabled: false` on its reads.
 *
 * ⚠ IT GOES THROUGH `controlsFor` LIKE EVERY CONTROL MAP, so `false` is only ever reached on a
 * KNOWN answer. Pending, unreadable and identity-unknown return true for every panel: the chart
 * renders exactly as it does today, fires exactly the reads it fires today, and says nothing
 * about access it has not established. That is the same rule the controls obey and the same one
 * `<ScreenRoute>` obeys one level up, so the three can never disagree about a single person.
 *
 * ⚠ AND `false` MUST REACH THE QUERY, not only the markup. A panel that renders the sentence
 * while its `useQuery` still runs fires the refused read anyway — and `withOfflineFallback` used
 * to serve the local replica for a 403, which is how a revoked person kept reading records off
 * their own device (INV-108 (a)). `isServerUnreachable` closed the fallback; `enabled: false`
 * closes the request. Both, because the chart fires sixteen reads on open and a narrowed person
 * would otherwise spend every visit generating 403s.
 */
export const patientChartPanels = (state: ControlAccess): Readonly<Record<ChartPanelKey, boolean>> => {
  const has = controlsFor(state);
  const map = {} as Record<ChartPanelKey, boolean>;
  for (const entry of Object.values(CHART_PANELS)) {
    map[entry.key] = entry.view.every((slug) => has(slug));
  }
  return Object.freeze(map);
};

/**
 * PRACTICE SETTINGS — `pages/Settings/PracticeSettings.tsx`: the practice form, the logo and the
 * "Lock screen (Beta)" select, whose writes share one gate. clinics.controller.ts `updateClinic`
 * :68-70 and `updateWorkstationLock` :41-43, uploads.controller.ts clinic-logo :25-27 and :52-54.
 *
 * ⚠ A ROLE TERM, because all four handlers declare @Roles('OWNER', 'ADMIN') AND
 * @RequirePermissions('manage:practice_settings'), and VET/TECH hold that slug by default: the slug
 * alone would offer clinicians a save the API refuses. An unknown identity decides nothing
 * (INV-107); a known role decides even while the permission read is pending. Listed by file in
 * `pageControls.wiring.test.ts` ALLOWED.
 */
export const practiceSettingsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const ownerOrAdmin =
    state.claims === null || state.claims?.role === 'OWNER' || state.claims?.role === 'ADMIN';
  return freeze({
    /** Save Changes, the logo buttons and the lock-screen select (a read-only page otherwise). */
    edit: ownerOrAdmin && has('manage:practice_settings'),
  });
};

/**
 * STABLEDOCS SETTINGS — `pages/Settings/StableDocsSettings.tsx`. PUT /stabledocs/settings declares
 * `manage:practice_settings` ALONE (stabledocs.controller.ts `updateSettings`), with no @Roles, so
 * unlike the practice form above there is no role term. The tab also has to READ the settings, and
 * GET /stabledocs/settings declares `view:medical_records` (senders read the wording too), so the tab
 * needs both: with the edit slug alone it would only ever show a load error.
 */
export const stableDocsSettingsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** The Settings tab, every field in it and the Save button. */
    edit: has('manage:practice_settings') && has('view:medical_records'),
  });
};

/**
 * CALENDAR SETTINGS — `pages/Appointments/CalendarSettings.tsx`. Its type colours write
 * PUT /service-catalog/:id, which declares @Roles('OWNER', 'ADMIN') AND
 * @RequirePermissions('manage:pricing') (service-catalog.controller.ts), so both terms are mirrored,
 * as in practiceSettingsControls. The person's own settings in the same panel are ungated.
 */
export const calendarSettingsControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  const ownerOrAdmin =
    state.claims === null || state.claims?.role === 'OWNER' || state.claims?.role === 'ADMIN';
  return freeze({
    /**
     * Add an appointment type and change a type's colour: POST and PUT /service-catalog, both
     * @Roles('OWNER', 'ADMIN') and manage:pricing (service-catalog.controller.ts:20-22, :47-48).
     */
    manageTypes: ownerOrAdmin && has('manage:pricing'),
  });
};

/**
 * TEAM MEMBERS — `pages/Settings/UserManagement.tsx`. The tab itself is `manage:users` (its early
 * return), so these are the row actions whose gate is not simply the tab's.
 */
export const teamMembersControls = (state: ControlAccess) => {
  const has = controlsFor(state);
  return freeze({
    /** "Clear PIN" → users.controller.ts `clearApprovalPin` :293-294 (`manage:users`). */
    clearPin: has(TEAM_MEMBERS_PERMISSION),
    /** "Permissions" → user-permissions.controller.ts `putUserPermissions` :72-73 (and its reads). */
    editPermissions: has('manage:roles_permissions'),
  });
};

/**
 * Does a row of controls have anything left in it? Used by the Dashboard so a quick-action row
 * whose every button is hidden leaves no empty frame behind — an empty bordered strip reads as a
 * rendering bug rather than as "nothing here for you".
 */
export const anyControl = (...flags: boolean[]): boolean => flags.some(Boolean);
